import React, { useState } from "react";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Spinner } from "@/components/ui/spinner";
import { useReportStrategyPineRequirements } from "../../hooks/useReportStrategyPineRequirements";
import { PineRequirementsContent } from "./PineRequirementsContent";
import {
  STRATEGY_PINE_SECTION_DESC_CLASS,
  STRATEGY_PINE_SECTION_LABEL_CLASS,
} from "./StrategyPineSectionCard";
import { STRATEGY_PINE_ALERT_CLASS, StrategyPineAlertIcon } from "./strategyPineAlertUi";

export type StrategyPineHelpPanelProps = {
  title?: string;
  subtitle?: string;
};

export function StrategyPineHelpPanel({
  title = "Pinescript & TradingView help",
  subtitle = "Parse stages and compose modes",
}: StrategyPineHelpPanelProps): React.ReactElement {
  const { data, loading, error } = useReportStrategyPineRequirements();
  const [expanded, setExpanded] = useState("");

  const isOpen = expanded === "help";

  return (
    <div data-strategy-pine-help-panel="" className="w-full min-w-0">
      <Accordion
        type="single"
        collapsible
        value={expanded}
        onValueChange={(value) => setExpanded(value ?? "")}
        className="space-y-0 border-0"
      >
        <AccordionItem
          value="help"
          data-strategy-pine-help-item=""
          className="border-border rounded-lg border px-3 not-last:border-b"
        >
          <AccordionTrigger
            className="items-start py-3 font-medium hover:no-underline"
            aria-label={isOpen ? `Collapse ${title}` : `Expand ${title}`}
          >
            <div className="flex w-full min-w-0 flex-col gap-1 pr-2 text-left">
              <Field className="gap-1">
                <FieldLabel className={STRATEGY_PINE_SECTION_LABEL_CLASS}>{title}</FieldLabel>
                <FieldDescription className={STRATEGY_PINE_SECTION_DESC_CLASS}>
                  {subtitle}
                </FieldDescription>
              </Field>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pt-0 pb-0 text-muted-foreground">
            {loading ? (
              <div className="flex items-center gap-2 py-2">
                <Spinner className="size-4 text-muted-foreground" aria-hidden />
                <span className={STRATEGY_PINE_SECTION_DESC_CLASS}>Loading help…</span>
              </div>
            ) : error ? (
              <Alert variant="warning" className={STRATEGY_PINE_ALERT_CLASS}>
                <StrategyPineAlertIcon tone="warning" />
                <AlertDescription className={STRATEGY_PINE_SECTION_DESC_CLASS}>
                  {error}
                </AlertDescription>
              </Alert>
            ) : (
              <PineRequirementsContent data={data} />
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
